import { useState } from 'react';

import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import useSWR from 'swr';

import Table from '@/components/RTable';
import useCurrency from '@/contexts/useCurrency';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
} from '@/types/simulations';
import { converToMillion, formatNumberWithCommas } from '@/utils';

import StyledProfit from './styles';

interface DataType {
  key: React.Key;
  total?: string | JSX.Element;
  energyAmount?: string;
  unit?: string;
  averagePrice?: string;
  charge?: string;
}

const ProfitAndLossTableYearly = () => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const symbol = currencySymbol || '$';
  const resultId = useParams<Record<string, string>>();
  const { data: financialData } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      `/simulation/${resultId.resultId}/yearly_financial_analysis/`
    );
  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId.resultId}/`
  );
  const [currentPage, setCurrentPage] = useState(1);

  const handleChange = (page: number) => {
    setCurrentPage(page);
  };

  let years: number[] = [];

  financialData?.graphData.forEach((item) => {
    years.push(item.year);
  });

  years = [...new Set(years)].sort((a, b) => a - b);

  if (!financialData || !simulationData) {
    return null;
  }

  const generateDataSource = (page: number): DataType[] => {
    if (page === 1) {
      // Total page using simulationData
      return [
        {
          key: '1',
          total: t('result.finance.profitAndLoss.tables.rows.yearly.storage'),
        },
        {
          key: '2',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromGeneratedAndStored'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              simulationData.result.soldEnergyFromDischargePlant.toFixed(2)
            )
          ),
          unit: 'MWh',
          averagePrice:
            simulationData.result.averageSalesPriceFromDischargePlant.toFixed(
              2
            ) + ` ${currencySymbol}`,
          charge:
            converToMillion(simulationData.result.revenueFromDischargePlant) +
            ` ${currencySymbol}`,
        },
        {
          key: '3',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromPurchasedAndStored'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              simulationData.result.soldEnergyFromDischargePurchased.toFixed(2)
            )
          ),
          unit: 'MWh',
          averagePrice:
            simulationData.result.averageSalesPriceFromDischargePurchased.toFixed(
              2
            ) + ` ${currencySymbol}`,
          charge:
            converToMillion(
              simulationData.result.revenueFromDischargePurchased
            ) + ` ${currencySymbol}`,
        },
        {
          key: '4',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromStorage'
          ),
          energyAmount: formatNumberWithCommas(
            Number(simulationData.result.soldEnergyFromDischarge.toFixed(2))
          ),
          unit: 'MWh',
          averagePrice:
            simulationData.result.averageSalesPriceFromDischarge.toFixed(2) +
            ` ${currencySymbol}`,
          charge:
            converToMillion(simulationData.result.revenueFromDischarge) +
            ` ${currencySymbol}`,
        },
        {
          key: '5',
          total: <div style={{ opacity: 0 }}>A</div>,
        },
        {
          key: '6',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.offeredCapacityForPfc'
          ),
          energyAmount: formatNumberWithCommas(
            Number(simulationData.result.offeredCapacityOfPfc.toFixed(2))
          ),
          unit: 'MW',
          averagePrice:
            simulationData.result.averagePriceOfPfc.toFixed(2) +
            ` ${currencySymbol}`,
          charge:
            converToMillion(
              simulationData.result.revenueFromOfferedCapacityOfPfc
            ) + ` ${currencySymbol}`,
        },
        {
          key: '7',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.offeredCapacityForSfc'
          ),
          energyAmount: formatNumberWithCommas(
            Number(simulationData.result.offeredCapacityOfSfc.toFixed(2))
          ),
          unit: 'MW',
          averagePrice:
            simulationData.result.averagePriceOfSfc.toFixed(2) +
            ` ${currencySymbol}`,
          charge:
            converToMillion(
              simulationData.result.revenueFromOfferedCapacityOfSfc
            ) + ` ${currencySymbol}`,
        },
        {
          key: '8',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.offeredCapacityForAncillaryServices'
          ),
          energyAmount: formatNumberWithCommas(
            Number(simulationData.result.totalOfferedCapacity.toFixed(2))
          ),
          unit: 'MW',
          averagePrice:
            simulationData.result.averagePriceOfAncillaryServices.toFixed(2) +
            ` ${currencySymbol}`,
          charge:
            converToMillion(simulationData.result.revenueFromOfferedCapacity) +
            ` ${currencySymbol}`,
        },
        {
          key: '9',
          total: <div style={{ opacity: 0 }}>A</div>,
        },
        {
          key: '10',
          total: t('result.finance.profitAndLoss.tables.rows.yearly.plant'),
        },
        {
          key: '11',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromPlant'
          ),
          energyAmount: formatNumberWithCommas(
            Number(simulationData.result.soldEnergyFromPlant.toFixed(2))
          ),
          unit: 'MWh',
          averagePrice:
            simulationData.result.averageSalesPriceFromPlant.toFixed(2) +
            ` ${currencySymbol}`,

          charge:
            converToMillion(simulationData.result.revenueFromPlant) +
            ` ${currencySymbol}`,
        },
        {
          key: '12',
          total: <div style={{ opacity: 0 }}>A</div>,
        },
        {
          key: '13',
          total: t('result.finance.profitAndLoss.tables.rows.yearly.cost'),
        },
        {
          key: '14',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.energyPurchased'
          ),
          energyAmount: formatNumberWithCommas(
            Number(simulationData.result.purchasedEnergy.toFixed(2))
          ),
          unit: 'MWh',
          averagePrice:
            simulationData.result.averageCostOfPurchasedEnergy.toFixed(2) +
            ` ${currencySymbol}`,
          charge:
            converToMillion(simulationData.result.costOfPurchasedEnergy) +
            ` ${currencySymbol}`,
        },
      ];
    } else {
      // Financial data for other pages
      return [
        {
          key: '1',
          total: t('result.finance.profitAndLoss.tables.rows.yearly.storage'),
        },
        {
          key: '2',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromGeneratedAndStored'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[
                currentPage - 2
              ].soldEnergyFromDischargePlant.toFixed(2)
            )
          ),
          unit: 'MWh',
          averagePrice:
            financialData.graphData[
              currentPage - 2
            ].averageSalesPriceFromDischargePlant.toFixed(2) +
            ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2].revenueFromDischargePlant
            ) + ` ${currencySymbol}`,
        },
        {
          key: '3',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromPurchasedAndStored'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[
                currentPage - 2
              ].soldEnergyFromDischargePurchased.toFixed(2)
            )
          ),
          unit: 'MWh',
          averagePrice:
            financialData.graphData[
              currentPage - 2
            ].averageSalesPriceFromDischargePurchased.toFixed(2) +
            ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2].batteryCapex
            ) + ` ${currencySymbol}`,
        },
        {
          key: '4',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromStorage'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[
                currentPage - 2
              ].soldEnergyFromDischarge.toFixed(2)
            )
          ),
          unit: 'MWh',
          averagePrice:
            financialData.graphData[
              currentPage - 2
            ].averageSalesPriceFromDischarge.toFixed(2) + ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2].revenueFromDischarge
            ) + ` ${currencySymbol}`,
        },
        {
          key: '5',
          total: <div style={{ opacity: 0 }}>A</div>,
        },
        {
          key: '6',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.offeredCapacityForPfc'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[
                currentPage - 2
              ].offeredCapacityOfPfc.toFixed(2)
            )
          ),
          unit: 'MW',
          averagePrice:
            financialData.graphData[currentPage - 2].averagePriceOfPfc.toFixed(
              2
            ) + ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2]
                .revenueFromOfferedCapacityOfPfc
            ) + ` ${currencySymbol}`,
        },
        {
          key: '7',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.offeredCapacityForSfc'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[
                currentPage - 2
              ].offeredCapacityOfSfc.toFixed(2)
            )
          ),
          unit: 'MW',
          averagePrice:
            financialData.graphData[currentPage - 2].averagePriceOfSfc.toFixed(
              2
            ) + ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2]
                .revenueFromOfferedCapacityOfSfc
            ) + ` ${currencySymbol}`,
        },
        {
          key: '8',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.offeredCapacityForAncillaryServices'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[
                currentPage - 2
              ].totalOfferedCapacity.toFixed(2)
            )
          ),
          unit: 'MW',
          averagePrice:
            financialData.graphData[
              currentPage - 2
            ].averagePriceOfAncillaryServices.toFixed(2) + ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2].batteryCapex
            ) + ` ${currencySymbol}`,
        },
        {
          key: '9',
          total: <div style={{ opacity: 0 }}>A</div>,
        },
        {
          key: '10',
          total: t('result.finance.profitAndLoss.tables.rows.yearly.plant'),
        },
        {
          key: '11',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.soldEnergyFromPlant'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[
                currentPage - 2
              ].soldEnergyFromPlant.toFixed(2)
            )
          ),
          unit: 'MWh',
          averagePrice:
            financialData.graphData[
              currentPage - 2
            ].averageSalesPriceFromPlant.toFixed(2) + ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2].revenueFromPlant
            ) + ` ${currencySymbol}`,
        },
        {
          key: '12',
          total: <div style={{ opacity: 0 }}>A</div>,
        },
        {
          key: '13',
          total: t('result.finance.profitAndLoss.tables.rows.yearly.cost'),
        },
        {
          key: '14',
          total: t(
            'result.finance.profitAndLoss.tables.rows.yearly.energyPurchased'
          ),
          energyAmount: formatNumberWithCommas(
            Number(
              financialData.graphData[currentPage - 2].purchasedEnergy.toFixed(
                2
              )
            )
          ),
          unit: 'MWh',
          averagePrice:
            financialData.graphData[
              currentPage - 2
            ].averageCostOfPurchasedEnergy.toFixed(2) + ` ${currencySymbol}`,
          charge:
            converToMillion(
              financialData.graphData[currentPage - 2].costOfPurchasedEnergy
            ) + ` ${currencySymbol}`,
        },
      ];
    }
  };

  const generateColumns = (firstColumnTitle: string): ColumnsType<DataType> => [
    {
      title: firstColumnTitle,
      dataIndex: 'total',
      key: 'total',
      width: '24%',
    },
    {
      title: t('result.finance.profitAndLoss.tables.columns.energyAmount'),
      dataIndex: 'energyAmount',
      key: 'energyAmount',
      width: '19%',
    },
    {
      title: t('result.finance.profitAndLoss.tables.columns.unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: '19%',
    },
    {
      title: t('result.finance.profitAndLoss.tables.columns.averagePrice', {
        symbol,
      }),
      dataIndex: 'averagePrice',
      key: 'averagePrice',
      width: '19%',
    },
    {
      title: t('result.finance.profitAndLoss.tables.columns.charge', {
        symbol,
      }),
      dataIndex: 'charge',
      key: 'charge',
      width: '19%',
    },
  ];

  const firstColumnTitle =
    currentPage === 1
      ? t('result.finance.profitAndLoss.tables.columns.total')
      : `Year ${currentPage - 1}`;
  const columns = generateColumns(firstColumnTitle);

  const totalPages = 1 + years.length;
  return (
    <>
      <StyledProfit>
        <Table
          dataSource={generateDataSource(currentPage)}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: 16,
            total: totalPages * 14,
            onChange: handleChange,
            showSizeChanger: false,
          }}
        />
      </StyledProfit>
    </>
  );
};

export default ProfitAndLossTableYearly;
